import { Suspense, lazy } from 'react';
import type { SlotEntry } from '@saas/ui';

/**
 * 画面の差し込み口（`@saas/ui` の Slot）に sales が出すもの。登録は apps/web/src/app/slots.ts（組み立て）だけが行う。
 * 見込み客の詳細（crm の画面・subject `lead`）に、商談化・追客カード・関連商談を差し込む。
 * 取引先の詳細（crm の画面・subject `customer`）に、取引先を出所にした追客カード（§15.3.3 休眠の掘り起こし）と
 * 「更新と受注」（§15.3.1 次の更新期限・最後の受注）を差し込む。
 * crm は sales を知らない（依存の向きを守る）
 *
 * | id | スロット | subjectType | order | 出すもの | context |
 * |---|---|---|---|---|---|
 * | sales.lead-convert | record.actions | lead | 10 | 「商談を作成」（商談化ダイアログ・§5.4） | `lead`（LeadDetail） |
 * | sales.lead-followups | record.panel | lead | 5 | 追客カード（Tasuki・§7。crm の「追客」メモのすぐ下） | `lead`（LeadDetail） |
 * | sales.lead-deals | record.panel | lead | 10 | 関連商談（一覧 + 関連商談を追加） | `lead`（LeadDetail） |
 * | sales.customer-followups | record.panel | customer | 20 | 「追客」（取引先を出所にした追客カード・§15.3.3。問い合わせ・見込み客（crm 30）より上） | なし（subjectId と title = 取引先名） |
 * | sales.customer-renewals | record.panel | customer | 25 | 「更新と受注」（次の更新期限・最後の受注・更新商談の候補の数・§15.3.1。追客のすぐ下） | なし（subjectId） |
 * | sales.project-followups | record.panel | project | 20 | 「引き継ぎ・追客」（案件を出所にした追客カード・受注後の引き継ぎ・gap-sales） | `code`（案件番号・任意）と title = 案件名 |
 *
 * sales.lead-followups の権限は「見込み客を読める」（crm.lead.read.*）。出所を読める人なら依頼でき、カードの閲覧・完了は
 * task が「ボードを読める ∧ 出所を読める」で絞る（sales の権限キーは増やさない）。
 * sales.customer-followups の権限は「取引先を読める」（crm.customer.read.*）。部品の中で sales の追客の口の入口
 * （商談か見込み客を読める）も確かめ、会社で task が無効なら（残った依頼が無い限り）何も出さない。
 * sales.customer-renewals の権限は「商談を読める」（sales.deal.read.*）。口（renewal-summary）はさらに取引先の閲覧を確かめ、
 * 読めない（403）・古いサーバ（404）なら部品は何も出さない。契約機能が無効な会社では「最後の受注」だけを出す。
 * sales.project-followups の権限は追客の口の入口と同じ（商談・見込み客・取引先・案件のどれかを読める）。案件の閲覧はサーバが registry の 'project' で確かめ、
 * 会社で task が無効なら（残った依頼が無い限り）何も出さない
 */
const LeadConvertAction = lazy(() => import('./components/LeadConvertAction.js'));
const LeadDealsPanel = lazy(() => import('./components/LeadDealsPanel.js'));
const LeadFollowupsPanel = lazy(() => import('./components/followups/LeadFollowupsPanel.js'));
const CustomerFollowupsPanel = lazy(() => import('./components/followups/CustomerFollowupsPanel.js'));
const CustomerRenewalsPanel = lazy(() => import('./components/renewals/CustomerRenewalsPanel.js'));
const ProjectFollowupsPanel = lazy(() => import('./components/followups/ProjectFollowupsPanel.js'));

/** 追客の口（/sales/followups・/sales/task-requests）の入口と同じ（sales-followup.controller.ts の SOURCE_READ） */
const FOLLOWUP_SOURCE_READ = [
  'sales.deal.read.own',
  'sales.deal.read.team',
  'sales.deal.read.all',
  'crm.lead.read.own',
  'crm.lead.read.team',
  'crm.lead.read.all',
  'crm.customer.read.own',
  'crm.customer.read.team',
  'crm.customer.read.all',
  'project.project.read.own',
  'project.project.read.team',
  'project.project.read.all',
];

export const salesSlots: SlotEntry[] = [
  {
    id: 'sales.lead-convert',
    module: 'sales',
    slot: 'record.actions',
    subjectTypes: ['lead'],
    permission: ['crm.lead.convert'],
    order: 10,
    Component: (props) => (
      <Suspense fallback={null}>
        <LeadConvertAction {...props} />
      </Suspense>
    ),
  },
  {
    id: 'sales.lead-followups',
    module: 'sales',
    slot: 'record.panel',
    subjectTypes: ['lead'],
    permission: ['crm.lead.read.own', 'crm.lead.read.team', 'crm.lead.read.all'],
    order: 5,
    Component: (props) => (
      <Suspense fallback={null}>
        <LeadFollowupsPanel {...props} />
      </Suspense>
    ),
  },
  {
    id: 'sales.lead-deals',
    module: 'sales',
    slot: 'record.panel',
    subjectTypes: ['lead'],
    permission: ['sales.deal.read.own', 'sales.deal.read.team', 'sales.deal.read.all'],
    order: 10,
    Component: (props) => (
      <Suspense fallback={null}>
        <LeadDealsPanel {...props} />
      </Suspense>
    ),
  },
  {
    id: 'sales.customer-followups',
    module: 'sales',
    slot: 'record.panel',
    subjectTypes: ['customer'],
    permission: ['crm.customer.read.own', 'crm.customer.read.team', 'crm.customer.read.all'],
    order: 20,
    Component: (props) => (
      <Suspense fallback={null}>
        <CustomerFollowupsPanel {...props} />
      </Suspense>
    ),
  },
  {
    id: 'sales.customer-renewals',
    module: 'sales',
    slot: 'record.panel',
    subjectTypes: ['customer'],
    permission: ['sales.deal.read.own', 'sales.deal.read.team', 'sales.deal.read.all'],
    order: 25,
    Component: (props) => (
      <Suspense fallback={null}>
        <CustomerRenewalsPanel {...props} />
      </Suspense>
    ),
  },
  {
    // gap-sales（§7.1・§7.2）: 業務案件を出所にした追客カード（受注後の引き継ぎ・完了後フォロー）
    id: 'sales.project-followups',
    module: 'sales',
    slot: 'record.panel',
    subjectTypes: ['project'],
    permission: FOLLOWUP_SOURCE_READ,
    order: 20,
    Component: (props) => (
      <Suspense fallback={null}>
        <ProjectFollowupsPanel {...props} />
      </Suspense>
    ),
  },
];
